import {
  calculatePopulationStandardDeviation,
  clampCellBoundsToGrid,
  getGridValue,
  getPhysicalBoundsForCellBounds,
  validate2DArrayShape,
} from './algorithmShared'
import {
  SHORT_TERM_FEATURE_MIN_CELL_COUNT,
  TARGET_FEATURE_CORE_DFN_PERCENT,
  TARGET_FEATURE_GROW_DFN_PERCENT,
  TARGET_FEATURE_MAX_RECT_AREA_FEET_SQUARED,
  TARGET_FEATURE_MERGE_DISTANCE_FEET,
  TARGET_FEATURE_MIN_AREA_FEET_SQUARED,
} from './constants'
import {
  compareFeatureCellGroups,
  findDisjointSetRoot,
  getBoundsGapFeet,
  getCellBoundsAreaFeetSquared,
  getCellBoundsFromCells,
  getCellKey,
  getExpandedBoundsBucketKeys,
  getFeatureCellAreaFeetSquared,
  getFeatureCellBoundingAreaFeetSquared,
  getFeatureCellBounds,
  mergeFeatureBounds,
  sortFeatureCells,
} from './geometry'
import type {
  DetectShortTermFeatureRectanglesProps,
  DetectShortTermFeatureRectanglesResult,
  ShortTermCorrosionCellBounds,
  ShortTermCorrosionFeatureRectangle,
  ShortTermGridValues,
  ShortTermRateFeature,
  ShortTermRateFeatureCell,
  ShortTermRateFeatureGridMetadata,
  TargetFeatureCellGroup,
} from './types'

export function detectShortTermCorrosionFeatureRectangles({
  targetThicknessValues,
  targetDifferenceFromNominalValues,
  targetGrid,
  observationRectangles = [],
  targetFeatureCoreDfnPercent = TARGET_FEATURE_CORE_DFN_PERCENT,
  targetFeatureGrowDfnPercent = TARGET_FEATURE_GROW_DFN_PERCENT,
  minFeatureCellCount = SHORT_TERM_FEATURE_MIN_CELL_COUNT,
  minFeatureAreaFeetSquared = TARGET_FEATURE_MIN_AREA_FEET_SQUARED,
  targetFeatureMergeDistanceFeet = TARGET_FEATURE_MERGE_DISTANCE_FEET,
  targetFeatureMaxRectAreaFeetSquared = TARGET_FEATURE_MAX_RECT_AREA_FEET_SQUARED,
}: DetectShortTermFeatureRectanglesProps): DetectShortTermFeatureRectanglesResult {
  validate2DArrayShape(targetThicknessValues, targetGrid)
  validate2DArrayShape(targetDifferenceFromNominalValues, targetGrid)

  const normalizedObservationRectangles = observationRectangles
    .map((rectangle) => normalizeFeatureRectangleForGrid(rectangle, targetGrid))
    .filter(
      (rectangle): rectangle is ShortTermCorrosionFeatureRectangle =>
        !!rectangle
    )
  const excludedCellMask = new Uint8Array(
    targetGrid.xBinCount * targetGrid.yBinCount
  )

  for (const rectangle of normalizedObservationRectangles) {
    markCellBounds(excludedCellMask, rectangle.targetCellBounds, targetGrid)
  }

  const detectedFeatures = extractTargetShortTermCorrosionFeatures({
    differenceFromNominalValues: targetDifferenceFromNominalValues,
    thicknessValues: targetThicknessValues,
    grid: targetGrid,
    coreDfnPercent: targetFeatureCoreDfnPercent,
    growDfnPercent: targetFeatureGrowDfnPercent,
    minFeatureCellCount,
    minFeatureAreaFeetSquared,
    mergeDistanceFeet: targetFeatureMergeDistanceFeet,
    excludedCellMask,
  })
  const detectedRectangles = detectedFeatures.flatMap((feature, index) =>
    splitDetectedFeatureIntoRectangles({
      feature,
      featureIndex: index,
      grid: targetGrid,
      differenceFromNominalValues: targetDifferenceFromNominalValues,
      minFeatureCellCount,
      minFeatureAreaFeetSquared,
      maxRectAreaFeetSquared: targetFeatureMaxRectAreaFeetSquared,
    })
  )
  const labeledDetectedRectangles =
    withSequentialDetectedFeatureLabels(detectedRectangles)

  return {
    featureRectangles: [
      ...normalizedObservationRectangles,
      ...labeledDetectedRectangles,
    ],
  }
}

export function extractTargetShortTermCorrosionFeatures({
  differenceFromNominalValues,
  thicknessValues,
  grid,
  coreDfnPercent = TARGET_FEATURE_CORE_DFN_PERCENT,
  growDfnPercent = TARGET_FEATURE_GROW_DFN_PERCENT,
  minFeatureCellCount = SHORT_TERM_FEATURE_MIN_CELL_COUNT,
  minFeatureAreaFeetSquared = TARGET_FEATURE_MIN_AREA_FEET_SQUARED,
  mergeDistanceFeet = TARGET_FEATURE_MERGE_DISTANCE_FEET,
  excludedCellMask,
}: {
  differenceFromNominalValues: ShortTermGridValues
  thicknessValues: ShortTermGridValues
  grid: ShortTermRateFeatureGridMetadata
  coreDfnPercent?: number
  growDfnPercent?: number
  minFeatureCellCount?: number
  minFeatureAreaFeetSquared?: number
  mergeDistanceFeet?: number
  excludedCellMask?: Uint8Array
}): ShortTermRateFeature[] {
  validate2DArrayShape(differenceFromNominalValues, grid)
  validate2DArrayShape(thicknessValues, grid)

  const isCoreCell = new Uint8Array(grid.xBinCount * grid.yBinCount)
  const isGrowableCell = new Uint8Array(isCoreCell.length)
  for (let y = 0; y < grid.yBinCount; y += 1) {
    for (let x = 0; x < grid.xBinCount; x += 1) {
      const value = getGridValue(differenceFromNominalValues, grid, x, y)
      const thickness = getGridValue(thicknessValues, grid, x, y)
      if (!Number.isFinite(value) || !Number.isFinite(thickness)) continue

      const index = getFlatCellIndex(x, y, grid.xBinCount)
      if (excludedCellMask?.[index]) continue
      if (value <= growDfnPercent) {
        isGrowableCell[index] = 1
      }
      if (value <= coreDfnPercent) {
        isCoreCell[index] = 1
      }
    }
  }

  const isVisitedCell = new Uint8Array(isCoreCell.length)
  const featureCellGroups: TargetFeatureCellGroup[] = []
  for (let y = 0; y < grid.yBinCount; y += 1) {
    for (let x = 0; x < grid.xBinCount; x += 1) {
      const index = getFlatCellIndex(x, y, grid.xBinCount)
      if (!isGrowableCell[index] || isVisitedCell[index]) continue

      const group = collectConnectedFeatureCellGroup({
        isSelectedCell: isGrowableCell,
        isCoreCell,
        isVisitedCell,
        grid,
        x,
        y,
      })
      if (!group.hasCoreCell) continue

      featureCellGroups.push(group)
    }
  }

  return mergeNearbyFeatureCellGroups({
    featureCellGroups,
    grid,
    mergeDistanceFeet,
  })
    .filter(
      (featureCells) =>
        featureCells.length >= minFeatureCellCount &&
        getFeatureCellBoundingAreaFeetSquared(featureCells, grid) >=
          minFeatureAreaFeetSquared
    )
    .map((featureCells, index) =>
      buildShortTermCorrosionFeature({
        id: `feature-${index + 1}`,
        cells: featureCells,
        differenceFromNominalValues,
        thicknessValues,
        grid,
      })
    )
}

function collectConnectedFeatureCellGroup({
  isSelectedCell,
  isCoreCell,
  isVisitedCell,
  grid,
  x,
  y,
}: {
  isSelectedCell: Uint8Array
  isCoreCell: Uint8Array
  isVisitedCell: Uint8Array
  grid: ShortTermRateFeatureGridMetadata
  x: number
  y: number
}): TargetFeatureCellGroup {
  const cells: ShortTermRateFeatureCell[] = []
  const stack = [getFlatCellIndex(x, y, grid.xBinCount)]
  let hasCoreCell = false

  isVisitedCell[stack[0]] = 1

  while (stack.length > 0) {
    const cellIndex = stack.pop()!
    const cell = {
      x: cellIndex % grid.xBinCount,
      y: Math.floor(cellIndex / grid.xBinCount),
    }
    cells.push(cell)
    hasCoreCell = hasCoreCell || isCoreCell[cellIndex] === 1

    for (let yOffset = -1; yOffset <= 1; yOffset += 1) {
      for (let xOffset = -1; xOffset <= 1; xOffset += 1) {
        if (xOffset === 0 && yOffset === 0) continue

        const nextX = cell.x + xOffset
        const nextY = cell.y + yOffset
        if (
          nextX < 0 ||
          nextX >= grid.xBinCount ||
          nextY < 0 ||
          nextY >= grid.yBinCount
        ) {
          continue
        }

        const nextIndex = getFlatCellIndex(nextX, nextY, grid.xBinCount)
        if (isVisitedCell[nextIndex] || !isSelectedCell[nextIndex]) continue

        isVisitedCell[nextIndex] = 1
        stack.push(nextIndex)
      }
    }
  }

  const sortedCells = sortFeatureCells(cells)

  return {
    cells: sortedCells,
    bounds: getFeatureCellBounds(sortedCells, grid),
    hasCoreCell,
  }
}

function buildShortTermCorrosionFeature({
  id,
  cells,
  differenceFromNominalValues,
  thicknessValues,
  grid,
}: {
  id: string
  cells: ShortTermRateFeatureCell[]
  differenceFromNominalValues: ShortTermGridValues
  thicknessValues: ShortTermGridValues
  grid: ShortTermRateFeatureGridMetadata
}): ShortTermRateFeature {
  let weightedXSum = 0
  let weightedYSum = 0
  let totalWeight = 0
  let unweightedXSum = 0
  let unweightedYSum = 0
  const featureThicknessValues: number[] = []

  for (const cell of cells) {
    const cellMinXFeet = grid.xStartFeet + cell.x * grid.binWidthFeet
    const cellMaxXFeet = cellMinXFeet + grid.binWidthFeet
    const cellMinYFeet = grid.yStartFeet + cell.y * grid.binHeightFeet
    const cellMaxYFeet = cellMinYFeet + grid.binHeightFeet
    const cellCenterXFeet = (cellMinXFeet + cellMaxXFeet) / 2
    const cellCenterYFeet = (cellMinYFeet + cellMaxYFeet) / 2
    const wastageWeight = Math.abs(
      getGridValue(differenceFromNominalValues, grid, cell.x, cell.y)
    )

    weightedXSum += cellCenterXFeet * wastageWeight
    weightedYSum += cellCenterYFeet * wastageWeight
    totalWeight += wastageWeight
    unweightedXSum += cellCenterXFeet
    unweightedYSum += cellCenterYFeet
    featureThicknessValues.push(
      getGridValue(thicknessValues, grid, cell.x, cell.y)
    )
  }

  const areaFeetSquared = getFeatureCellAreaFeetSquared(cells, grid)
  const bounds = getFeatureCellBounds(cells, grid)
  const centroidXFeet =
    totalWeight > 0 ? weightedXSum / totalWeight : unweightedXSum / cells.length
  const centroidYFeet =
    totalWeight > 0 ? weightedYSum / totalWeight : unweightedYSum / cells.length

  return {
    id,
    cells,
    thicknessValues: featureThicknessValues,
    bounds,
    centroidXFeet,
    centroidYFeet,
    areaFeetSquared,
    equivalentRadiusFeet: Math.sqrt(areaFeetSquared / Math.PI),
    binDiagonalFeet: Math.hypot(grid.binWidthFeet, grid.binHeightFeet),
  }
}

function mergeNearbyFeatureCellGroups({
  featureCellGroups,
  grid,
  mergeDistanceFeet,
}: {
  featureCellGroups: readonly TargetFeatureCellGroup[]
  grid: ShortTermRateFeatureGridMetadata
  mergeDistanceFeet: number
}): ShortTermRateFeatureCell[][] {
  if (featureCellGroups.length === 0) {
    return []
  }

  let parentIndexByIndex = featureCellGroups.map((_, index) => index)
  const rank = new Uint8Array(featureCellGroups.length)
  const setParentIndex = (index: number, parentIndex: number) => {
    const nextParentIndexByIndex = parentIndexByIndex.slice()
    nextParentIndexByIndex[index] = parentIndex
    parentIndexByIndex = nextParentIndexByIndex
  }
  const union = (firstIndex: number, secondIndex: number) => {
    const firstRootResult = findDisjointSetRoot(parentIndexByIndex, firstIndex)
    parentIndexByIndex = firstRootResult.parentIndexByIndex
    const firstRoot = firstRootResult.rootIndex

    const secondRootResult = findDisjointSetRoot(
      parentIndexByIndex,
      secondIndex
    )
    parentIndexByIndex = secondRootResult.parentIndexByIndex
    const secondRoot = secondRootResult.rootIndex
    if (firstRoot === secondRoot) return

    if (rank[firstRoot] < rank[secondRoot]) {
      setParentIndex(firstRoot, secondRoot)
    } else if (rank[firstRoot] > rank[secondRoot]) {
      setParentIndex(secondRoot, firstRoot)
    } else {
      setParentIndex(secondRoot, firstRoot)
      rank[firstRoot] += 1
    }
  }
  const buckets = new Map<string, number[]>()

  for (
    let groupIndex = 0;
    groupIndex < featureCellGroups.length;
    groupIndex += 1
  ) {
    const comparedGroupIndexes = new Set<number>()
    const bucketKeys = getExpandedBoundsBucketKeys({
      bounds: featureCellGroups[groupIndex].bounds,
      grid,
      expansionFeet: Math.max(0, mergeDistanceFeet),
    })

    for (const bucketKey of bucketKeys) {
      const bucket = buckets.get(bucketKey)
      if (!bucket) {
        buckets.set(bucketKey, [groupIndex])
        continue
      }

      for (const otherGroupIndex of bucket) {
        if (comparedGroupIndexes.has(otherGroupIndex)) continue
        comparedGroupIndexes.add(otherGroupIndex)

        if (
          getBoundsGapFeet(
            featureCellGroups[groupIndex].bounds,
            featureCellGroups[otherGroupIndex].bounds
          ) <= mergeDistanceFeet
        ) {
          union(groupIndex, otherGroupIndex)
        }
      }

      bucket.push(groupIndex)
    }
  }

  const groupsByRoot = new Map<number, TargetFeatureCellGroup>()

  for (
    let groupIndex = 0;
    groupIndex < featureCellGroups.length;
    groupIndex += 1
  ) {
    const rootResult = findDisjointSetRoot(parentIndexByIndex, groupIndex)
    parentIndexByIndex = rootResult.parentIndexByIndex
    const root = rootResult.rootIndex
    const existingGroup = groupsByRoot.get(root)
    const group = featureCellGroups[groupIndex]

    if (!existingGroup) {
      groupsByRoot.set(root, {
        cells: group.cells.slice(),
        bounds: group.bounds,
        hasCoreCell: group.hasCoreCell,
      })
      continue
    }

    for (const cell of group.cells) {
      existingGroup.cells.push(cell)
    }
    existingGroup.bounds = mergeFeatureBounds(
      existingGroup.bounds,
      group.bounds
    )
    existingGroup.hasCoreCell = existingGroup.hasCoreCell || group.hasCoreCell
  }

  return Array.from(groupsByRoot.values())
    .filter((group) => group.hasCoreCell)
    .map((group) => sortFeatureCells(group.cells))
    .toSorted(compareFeatureCellGroups)
}

function splitDetectedFeatureIntoRectangles({
  feature,
  featureIndex,
  grid,
  differenceFromNominalValues,
  minFeatureCellCount,
  minFeatureAreaFeetSquared,
  maxRectAreaFeetSquared,
}: {
  feature: ShortTermRateFeature
  featureIndex: number
  grid: ShortTermRateFeatureGridMetadata
  differenceFromNominalValues: ShortTermGridValues
  minFeatureCellCount: number
  minFeatureAreaFeetSquared: number
  maxRectAreaFeetSquared: number
}): ShortTermCorrosionFeatureRectangle[] {
  const featureCellBounds = getCellBoundsFromCells(feature.cells)
  const tileWidthCells = getMaxRectangleTileWidthCells({
    grid,
    maxRectAreaFeetSquared,
  })
  const tileHeightCells = getMaxRectangleTileHeightCells({
    grid,
    tileWidthCells,
    maxRectAreaFeetSquared,
  })
  const cellsByTile = new Map<string, ShortTermRateFeatureCell[]>()

  for (const cell of feature.cells) {
    const tileX = Math.floor((cell.x - featureCellBounds.minX) / tileWidthCells)
    const tileY = Math.floor(
      (cell.y - featureCellBounds.minY) / tileHeightCells
    )
    const tileKey = getCellKey(tileX, tileY)
    const tileCells = cellsByTile.get(tileKey)
    if (tileCells) {
      tileCells.push(cell)
    } else {
      cellsByTile.set(tileKey, [cell])
    }
  }

  const rectangles: ShortTermCorrosionFeatureRectangle[] = []
  const sortedTileGroups = Array.from(cellsByTile.values())
    .filter(
      (featureCells) =>
        featureCells.length >= minFeatureCellCount &&
        getFeatureCellBoundingAreaFeetSquared(featureCells, grid) >=
          minFeatureAreaFeetSquared
    )
    .map(sortFeatureCells)
    .toSorted(compareFeatureCellGroups)

  for (let tileIndex = 0; tileIndex < sortedTileGroups.length; tileIndex += 1) {
    const cells = sortedTileGroups[tileIndex]

    rectangles.push(
      createFeatureRectangleFromCells({
        id: `detected-cluster-${featureIndex + 1}-zone-${tileIndex + 1}`,
        source: 'detected',
        cells,
        seedCellCount: cells.length,
        severeDfnPercent: calculateSevereDfnPercentForCells(
          cells,
          differenceFromNominalValues,
          grid
        ),
        grid,
      })
    )
  }

  return rectangles
}

function withSequentialDetectedFeatureLabels(
  featureRectangles: readonly ShortTermCorrosionFeatureRectangle[]
): ShortTermCorrosionFeatureRectangle[] {
  return featureRectangles.map((rectangle, index) => ({
    ...rectangle,
    id: `detected-${index + 1}`,
    label: `Zone ${index + 1}`,
  }))
}

function calculateSevereDfnPercentForCells(
  cells: readonly ShortTermRateFeatureCell[],
  differenceFromNominalValues: ShortTermGridValues,
  grid: ShortTermRateFeatureGridMetadata
): number | undefined {
  const values: number[] = []

  for (const cell of cells) {
    const value = getGridValue(
      differenceFromNominalValues,
      grid,
      cell.x,
      cell.y
    )
    if (Number.isFinite(value)) values.push(value)
  }

  if (values.length === 0) return undefined

  const mean = values.reduce((sum, value) => sum + value, 0) / values.length

  return mean - 2 * calculatePopulationStandardDeviation(values, mean)
}

function getMaxRectangleTileWidthCells({
  grid,
  maxRectAreaFeetSquared,
}: {
  grid: ShortTermRateFeatureGridMetadata
  maxRectAreaFeetSquared: number
}): number {
  const maxRectSideFeet = Math.sqrt(Math.max(0, maxRectAreaFeetSquared))
  const maxWidthCells = Math.max(
    1,
    Math.floor(Math.max(grid.binWidthFeet, maxRectSideFeet) / grid.binWidthFeet)
  )
  const maxAreaCells = Math.max(
    1,
    Math.floor(
      Math.max(grid.binWidthFeet * grid.binHeightFeet, maxRectAreaFeetSquared) /
        (grid.binWidthFeet * grid.binHeightFeet)
    )
  )

  return Math.max(1, Math.min(maxWidthCells, maxAreaCells))
}

function getMaxRectangleTileHeightCells({
  grid,
  tileWidthCells,
  maxRectAreaFeetSquared,
}: {
  grid: ShortTermRateFeatureGridMetadata
  tileWidthCells: number
  maxRectAreaFeetSquared: number
}): number {
  const maxRectSideFeet = Math.sqrt(Math.max(0, maxRectAreaFeetSquared))
  const maxHeightCells = Math.max(
    1,
    Math.floor(
      Math.max(grid.binHeightFeet, maxRectSideFeet) / grid.binHeightFeet
    )
  )
  const maxAreaCells = Math.max(
    1,
    Math.floor(
      Math.max(grid.binWidthFeet * grid.binHeightFeet, maxRectAreaFeetSquared) /
        (grid.binWidthFeet * grid.binHeightFeet)
    )
  )

  return Math.max(
    1,
    Math.min(maxHeightCells, Math.floor(maxAreaCells / tileWidthCells))
  )
}

function createFeatureRectangleFromCells({
  id,
  source,
  label,
  cells,
  seedCellCount,
  severeDfnPercent,
  grid,
}: {
  id: string
  source: ShortTermCorrosionFeatureRectangle['source']
  label?: string
  cells: readonly ShortTermRateFeatureCell[]
  seedCellCount: number
  severeDfnPercent?: number
  grid: ShortTermRateFeatureGridMetadata
}): ShortTermCorrosionFeatureRectangle {
  return createFeatureRectangleFromCellBounds({
    id,
    source,
    label,
    targetCellBounds: getCellBoundsFromCells(cells),
    seedCellCount,
    severeDfnPercent,
    grid,
  })
}

export function createFeatureRectangleFromCellBounds({
  id,
  source,
  label,
  targetCellBounds,
  seedCellCount,
  severeDfnPercent,
  grid,
}: {
  id: string
  source: ShortTermCorrosionFeatureRectangle['source']
  label?: string
  targetCellBounds: ShortTermCorrosionCellBounds
  seedCellCount: number
  severeDfnPercent?: number
  grid: ShortTermRateFeatureGridMetadata
}): ShortTermCorrosionFeatureRectangle {
  const clampedCellBounds = clampCellBoundsToGrid(targetCellBounds, grid)

  return {
    id,
    source,
    label,
    targetCellBounds: clampedCellBounds,
    targetBoundsFeet: getPhysicalBoundsForCellBounds(clampedCellBounds, grid),
    seedCellCount,
    areaFeetSquared: getCellBoundsAreaFeetSquared(clampedCellBounds, grid),
    ...(Number.isFinite(severeDfnPercent) ? { severeDfnPercent } : {}),
  }
}

function normalizeFeatureRectangleForGrid(
  rectangle: ShortTermCorrosionFeatureRectangle,
  grid: ShortTermRateFeatureGridMetadata
): ShortTermCorrosionFeatureRectangle | undefined {
  const targetCellBounds = clampCellBoundsToGrid(
    rectangle.targetCellBounds,
    grid
  )
  if (!isValidCellBounds(targetCellBounds)) return undefined

  return {
    ...rectangle,
    targetCellBounds,
    targetBoundsFeet: getPhysicalBoundsForCellBounds(targetCellBounds, grid),
    areaFeetSquared: getCellBoundsAreaFeetSquared(targetCellBounds, grid),
  }
}

function markCellBounds(
  mask: Uint8Array,
  cellBounds: ShortTermCorrosionCellBounds,
  grid: ShortTermRateFeatureGridMetadata
) {
  const clampedCellBounds = clampCellBoundsToGrid(cellBounds, grid)
  if (!isValidCellBounds(clampedCellBounds)) return

  for (let y = clampedCellBounds.minY; y <= clampedCellBounds.maxY; y += 1) {
    for (let x = clampedCellBounds.minX; x <= clampedCellBounds.maxX; x += 1) {
      mask[getFlatCellIndex(x, y, grid.xBinCount)] = 1
    }
  }
}

function isValidCellBounds(cellBounds: ShortTermCorrosionCellBounds): boolean {
  return (
    Number.isFinite(cellBounds.minX) &&
    Number.isFinite(cellBounds.maxX) &&
    Number.isFinite(cellBounds.minY) &&
    Number.isFinite(cellBounds.maxY) &&
    cellBounds.minX <= cellBounds.maxX &&
    cellBounds.minY <= cellBounds.maxY
  )
}

function getFlatCellIndex(x: number, y: number, xBinCount: number): number {
  return y * xBinCount + x
}
